import { useEffect, useRef, useState } from 'react';

import { useFocusTrap } from '@mantine/hooks';

import { createRGBAWithHex } from '@/utils/Color';

import ModalBody from './ModalBody';
import ModalBodySplit from './ModalBodySplit';
import ModalContext from './ModalContext';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import ModalPortal from './ModalPortal';

import styled, { createGlobalStyle } from 'styled-components';

const SIZE = {
  xs: 320,
  sm: 560,
  md: 720,
  lg: 920,
  xl: 1200,
  fullscreen: '100vw',
} as const;

type ModalSizeType = keyof typeof SIZE;

const DRAWER_OVERLAY_CLASS_NAME = 'mantine-Drawer-overlay';

type ModalProps = {
  size?: ModalSizeType;
  gap?: number;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  zIndex?: number;
  disableEscapeKeyDown?: boolean;
};

const Modal = ({
  size = 'md',
  gap = 0,
  open,
  onClose,
  children,
  zIndex = 800,
  disableEscapeKeyDown = false,
}: ModalProps) => {
  const modalRootRef = useRef<HTMLDivElement>(null);

  const [showHeaderBorder, setShowHeaderBorder] = useState(false);
  const [showFooterBorder, setShowFooterBorder] = useState(false);
  const [hasDrawerOverlay, setHasDrawerOverlay] = useState(false);

  const isFullScreen = size === 'fullscreen';

  const handleHeaderBorderChange = (value: boolean) => {
    setShowHeaderBorder(value);
  };

  const handleFooterBorderChange = (value: boolean) => {
    setShowFooterBorder(value);
  };

  useEffect(() => {
    if (!open || disableEscapeKeyDown) {
      return;
    }

    const root = modalRootRef.current;
    if (!root) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      const isTopModal = root.parentElement?.lastElementChild === root;
      if (!isTopModal) {
        return;
      }

      event.stopPropagation();
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [open, onClose, disableEscapeKeyDown]);

  // 모달 backdrop과 Drawer 오버레이가 그대로 겹치면 dim이 두 겹이 되어, Drawer 패널만 한 겹을 받아
  // 주변보다 밝아 보이는 역전이 생긴다. 그래서 모달이 떠 있는 동안 Drawer 오버레이를 투명하게 만든다.
  useEffect(() => {
    if (!open) {
      return;
    }

    const syncDrawerOverlay = () => {
      setHasDrawerOverlay(Boolean(document.querySelector(`.${DRAWER_OVERLAY_CLASS_NAME}`)));
    };

    syncDrawerOverlay();

    // Drawer portal 컨테이너는 body 직계 자식으로 한 번만 붙고 Drawer.Overlay가 그 안에서 추가·제거되므로,
    // subtree까지 관찰해야 감지된다.
    const observer = new MutationObserver(syncDrawerOverlay);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();

      // 억제 스타일이 걷히면 오버레이가 200ms에 걸쳐 되살아나, 모달 backdrop은 이미 사라진 뒤라
      // 그동안 dim이 옅어졌다 돌아온다. 한 프레임만 transition을 끊어 같은 프레임에 복귀시킨다.
      const overlay = document.querySelector<HTMLElement>(`.${DRAWER_OVERLAY_CLASS_NAME}`);
      if (!overlay) {
        return;
      }

      overlay.style.transition = 'none';
      requestAnimationFrame(() => {
        overlay.style.transition = '';
      });
    };
  }, [open]);

  // 모달을 닫을 때 focus가 다음 top 모달로 이동하도록 함
  // 이 effect가 없으면 body로 focus가 빠짐
  useEffect(() => {
    if (!open) {
      return;
    }

    const parent = modalRootRef.current?.parentElement;

    return () => {
      const nextTop = parent?.lastElementChild;
      if (nextTop instanceof HTMLElement) {
        nextTop.focus();
      }
    };
  }, [open]);

  const focusTrapRef = useFocusTrap(open);

  return (
    <ModalPortal show={open}>
      <ModalContext.Provider
        value={{
          showHeaderBorder,
          showFooterBorder,
          onHeaderBorderChange: handleHeaderBorderChange,
          onFooterBorderChange: handleFooterBorderChange,
          onClose,
          isFullScreen,
        }}
      >
        <ModalRoot ref={modalRootRef} $fullScreen={isFullScreen} $zIndex={zIndex} tabIndex={-1}>
          {size !== 'fullscreen' && hasDrawerOverlay && <DrawerOverlaySuppressor />}
          {size !== 'fullscreen' && <ModalBackdrop />}
          <ModalContent
            $size={size}
            $gap={gap}
            $fullScreen={isFullScreen}
            ref={focusTrapRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
          >
            {children}
          </ModalContent>
        </ModalRoot>
      </ModalContext.Provider>
    </ModalPortal>
  );
};

// Mantine의 Transition이 오버레이에 inline opacity를 넣으므로 !important가 필요하다.
// transition까지 끊지 않으면 오버레이가 200ms에 걸쳐 사라지는 동안 모달 backdrop과 겹쳐
// dim이 0.5 → 0.75 → 0.5로 튄다.
const DrawerOverlaySuppressor = createGlobalStyle`
  .${DRAWER_OVERLAY_CLASS_NAME} {
    opacity: 0 !important;
    transition: none !important;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  background-color: ${({ theme }) => theme.mantineColors.gray[8]};
`;

const ModalRoot = styled.div<{ $fullScreen?: boolean; $zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: ${({ $fullScreen }) => ($fullScreen ? 'stretch' : 'center')};
  justify-content: ${({ $fullScreen }) => ($fullScreen ? 'stretch' : 'center')};

  // 여러 개 모달이 떠있을 때 마지막 모달만 backdrop 보이기
  &:not(:last-child) ${ModalBackdrop} {
    opacity: 0;
  }
`;

const ModalContent = styled.div<{ $size: ModalSizeType; $gap: number; $fullScreen: boolean }>`
  position: relative;
  width: ${({ $size, $fullScreen }) => ($fullScreen ? '100vw' : `${SIZE[$size]}px`)};
  min-width: ${({ $size, $fullScreen }) => ($fullScreen ? '100vw' : `${SIZE[$size]}px`)};
  height: ${({ $fullScreen }) => ($fullScreen ? '100vh' : 'auto')};
  max-height: ${({ $fullScreen }) => ($fullScreen ? '100vh' : 'calc(100vh - 56px * 2)')};
  background-color: ${({ theme, $fullScreen }) =>
    $fullScreen ? theme.mantineColors.background : theme.mantineColors.gray[1]};
  border-radius: ${({ $fullScreen }) => ($fullScreen ? '0' : '12px')};
  box-shadow: ${({ $fullScreen, theme }) =>
    $fullScreen ? 'none' : `0px 6px 20px 0px ${createRGBAWithHex(theme.mantineColors.gray[8], 0.15)}`};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: ${({ $gap }) => $gap ?? 0}px;
`;

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.BodySplit = ModalBodySplit;
Modal.Footer = ModalFooter;

export default Modal;
